import React from 'react';

import { element } from '@lumapps/classnames';
import { get as getConfig } from '@lumapps/constants';
import { AppId } from '@lumapps/constants/app';

import {
    USER_CARD_POPOVER_CLASSNAME,
    USER_CARD_POPOVER_CLOSE_ELEMENT,
    USER_CARD_POPOVER_MENU_ELEMENT,
} from '../constants';
import { MinimumViableUser, UserAccountType, UseUserOptions } from '../types';
import { useUser } from './useUser';

const DELAY = 500;

export interface UseUserCardPopoverResult {
    /**
     * Function to be called when the mouse leaves the user card popover.
     */
    onMouseLeave: (event?: React.KeyboardEvent | React.MouseEvent) => void;
    /**
     * Function to be called when the mouse enters the user card popover.
     */
    onMouseEnter: () => void;
    /** callback on clicking a trigger that opens the user card */
    onClick: () => void;
    /**
     * Boolean indicating whether the user data is currently loading.
     */
    isLoading: boolean;
    /**
     * The user profile data to be displayed in the card.
     */
    user: MinimumViableUser;
    /**
     * Boolean indicating whether the user card should be displayed.
     */
    showUserCard: boolean;
    /** whether the popover should be displayed as a dialog or not */
    asDialog: boolean;
    /** reference to the anchor element for the popover */
    anchorRef: React.RefObject<HTMLElement>;
}

export interface UseUserCardPopoverOptions extends UseUserOptions {
    /** custom delay for displaying the user card */
    delay?: number;
    /** whether the user card is displayed with a trigger or not */
    shouldUseTrigger?: boolean;
    /** classname to be used for validating on mouse leave / enter actions */
    targetClassname?: string[];
}

const { applicationId } = getConfig();

interface HandleMouseLeaveOptions {
    event: React.KeyboardEvent | React.MouseEvent | undefined;
    setShowUserCard: React.Dispatch<React.SetStateAction<boolean>>;
    setIsHovered: React.Dispatch<React.SetStateAction<boolean>>;
    setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
    anchorRef: React.RefObject<HTMLElement>;
    shouldUseTrigger: boolean;
    isFocused: boolean;
    targetClassname?: string[];
}
/**
 * Function to handle the mouse leave event. Extracted from the hook so it can be easily tested, but it should not be reused outside of the hook.
 */
export const handleMouseLeave = ({
    event,
    setShowUserCard,
    setIsHovered,
    setIsFocused,
    anchorRef,
    shouldUseTrigger,
    isFocused,
    targetClassname,
}: HandleMouseLeaveOptions) => {
    const target = event?.target as HTMLElement;

    const closePopover = () => {
        setShowUserCard(false);
        setIsHovered(false);
    };

    const resetFocus = () => {
        setIsFocused(false);
        anchorRef.current?.removeAttribute('data-focus-visible-added');
    };

    const setFocusVisible = () => {
        if (isFocused) {
            anchorRef.current?.setAttribute('data-focus-visible-added', '');
        }
    };

    const containsTargetClassname =
        targetClassname && targetClassname.some((classname) => target?.classList?.contains(classname));

    if (shouldUseTrigger) {
        if (event instanceof KeyboardEvent && event?.key === 'Escape') {
            event.stopPropagation();
            /**
             * If the event is a keyboard event, and it's the Escape key, close the popover and reset focus since
             * we need to set the focus back to the trigger element.
             */
            closePopover();
            setFocusVisible();
        } else if (
            event?.type === 'click' &&
            target?.classList?.contains(element(USER_CARD_POPOVER_CLASSNAME)(USER_CARD_POPOVER_CLOSE_ELEMENT))
        ) {
            event.stopPropagation();
            /**
             * If the event is a click event and the target is the close button (the cross), close the popover and reset focus since
             * we need to set the focus back to the trigger element.
             */
            closePopover();
            setFocusVisible();
        } else if (
            event?.type === 'mouseleave' &&
            (target?.classList?.contains(element(USER_CARD_POPOVER_CLASSNAME)(USER_CARD_POPOVER_MENU_ELEMENT)) ||
                (targetClassname && containsTargetClassname))
        ) {
            /**
             * If the event is a mouseleave event and the target is either the user card popover menu or the user avatar,
             * it means that the user left the popover. In that scenario, we need to set the hovered state to false so that
             * the popover can be closed after the delay. We remove focus in this scenario.
             */
            setIsHovered(false);
            resetFocus();
        } else if (event?.type === 'mousedown') {
            /**
             * If it is a mousedown, it could mean a click outside the popover. In that case, we need to set the hovered state to false,
             * close the user card and set back focus if the popover was opened with the keyboard
             */
            setIsHovered(false);
            setShowUserCard(false);
            setFocusVisible();
        } else {
            /**
             * By default, we leave the popover open for a while to allow the user to move the mouse
             */
            setIsHovered(false);
            resetFocus();
        }
    } else {
        setIsHovered(false);
        resetFocus();
    }
};

/**
 * A hook to manage the state and behavior of a user card popover.
 * It handles hovering, loading, and displaying user information.
 *
 * @param user The base user object to display in the popover.
 * @returns Options for the user card popover, including event handlers, loading state, and user profile data.
 */
export const useUserCardPopover = (
    user?: MinimumViableUser,
    options?: UseUserCardPopoverOptions,
): UseUserCardPopoverResult => {
    const { delay, enabled = true, shouldUseTrigger = false, targetClassname, ...restOfOptions } = options || {};
    const [isHovered, setIsHovered] = React.useState<boolean>(false);
    const [isFocused, setIsFocused] = React.useState<boolean>(false);
    const [showUserCard, setShowUserCard] = React.useState<boolean>(false);
    const anchorRef = React.useRef<HTMLElement>(null);
    const baseUser = {
        accountType: UserAccountType.GOOGLE,
        fullName: '',
        id: '',
        email: '',
        lastName: '',
        firstName: '',
        jobTitle: '',
        ...user,
    };

    const { data, isLoading } = useUser(baseUser, {
        staleTime: 2 * 60 * 1000, // 2 minutes,
        cacheTime: 5 * 60 * 1000, // 5 minutes
        ...restOfOptions,
        enabled: enabled && showUserCard,
    });

    const isWebview = applicationId === AppId.webview;
    // Disable on webview app or if neither the id nor the email is provided.
    const isUserCardDisplayable = !isWebview && (Boolean(user?.email) || Boolean(user?.id));

    /**
     * Handler function to set the hovered state to true.
     */
    const onMouseEnter = () => {
        setIsHovered(true);
    };

    const hideUserCardOnTimeout = React.useCallback(
        (forceShow?: boolean) => {
            return setTimeout(() => {
                if (isUserCardDisplayable) {
                    const shouldShowUserCard = forceShow === undefined ? isHovered : forceShow;
                    setShowUserCard(shouldShowUserCard);

                    if (forceShow !== undefined) {
                        setIsHovered(forceShow);
                    }
                }
            }, delay || DELAY);
        },
        [isHovered, delay, isUserCardDisplayable],
    );

    /**
     * Handler function to set the hovered state to false.
     */
    const onMouseLeave = (event?: React.KeyboardEvent | React.MouseEvent) => {
        handleMouseLeave({
            event,
            setShowUserCard,
            setIsHovered,
            setIsFocused,
            isFocused,
            anchorRef,
            shouldUseTrigger,
            targetClassname,
        });
    };

    const onClick = () => {
        setShowUserCard(true);
        setIsFocused(true);
    };

    /**
     * When either the trigger element or the popover itself hover status changes,
     * change status after given delay and clear previous timeout.
     */
    React.useEffect(() => {
        const openTimer = hideUserCardOnTimeout();

        // clear timeout
        return () => {
            if (openTimer) {
                clearTimeout(openTimer);
            }
        };
    }, [hideUserCardOnTimeout]);

    return {
        onMouseLeave,
        onMouseEnter,
        onClick,
        isLoading,
        user: data || baseUser,
        showUserCard,
        asDialog: isFocused && !isHovered,
        anchorRef,
    };
};
